import React from 'react';
import {Link} from 'react-router-dom';
import {AdminEmail, AdminSuppression} from '../../domain/admin';
import {EmailStatusBadge, EmailTypeBadge, SuppressionBadge} from './AdminBadges';
import {EmptyState, ErrorState, LoadingState, Timestamp} from './AdminPrimitives';

/**
 * The two global email views: every send, and the standing blocklist behind the failures.
 *
 * The log embeds the owning user, so a bounced login link is one click from the account it locked
 * out rather than a dead end — which is the whole reason this screen exists. Rows with no `user`
 * are real and not a bug: a setup email sent before provisioning finished has no account yet, so
 * the recipient address is all there is.
 */

const headerCellClass = 'px-4 py-2 font-medium';
const cellClass = 'px-4 py-2.5 align-top';

interface AdminEmailLogTableProps {
  emails: AdminEmail[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  hasFilters: boolean;
}

export const AdminEmailLogTable: React.FC<AdminEmailLogTableProps> = ({
  emails,
  isLoading,
  error,
  onRetry,
  hasFilters,
}) => {
  if (error && emails.length === 0) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (isLoading && emails.length === 0) {
    return <LoadingState message="Loading the email log…" />;
  }

  if (emails.length === 0) {
    return (
      <EmptyState
        message={hasFilters ? 'No email matches these filters.' : 'No email logged yet.'}
        hint={
          hasFilters
            ? undefined
            : 'Sends only appear here from the point email logging was added onwards.'
        }
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-left text-xs">
        <thead>
          <tr className="border-b border-gray-800 bg-gray-950/40 text-[11px] uppercase tracking-wide text-gray-500">
            <th scope="col" className={headerCellClass}>Sent</th>
            <th scope="col" className={headerCellClass}>Recipient</th>
            <th scope="col" className={headerCellClass}>Type</th>
            <th scope="col" className={headerCellClass}>Status</th>
            <th scope="col" className={headerCellClass}>Triggered by</th>
            <th scope="col" className={headerCellClass}>Provider event</th>
            <th scope="col" className={headerCellClass} />
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-800/60">
          {emails.map(row => (
            <tr key={row.id} className="transition-colors hover:bg-gray-900/60">
              <td className={`${cellClass} text-gray-400`}>
                <Timestamp iso={row.createdAt} />
              </td>

              <td className={`${cellClass} max-w-[240px]`}>
                <span className="block truncate text-gray-100" title={row.recipient}>
                  {row.recipient}
                </span>
                {/*
                  The user's address today can differ from the one we sent to — showing the account
                  name under the historical recipient keeps both facts on screen.
                */}
                {row.user?.name && (
                  <span className="block truncate text-[11px] text-gray-500">{row.user.name}</span>
                )}
              </td>

              <td className={cellClass}>
                <EmailTypeBadge emailType={row.emailType} />
              </td>

              <td className={cellClass}>
                <EmailStatusBadge status={row.status} />
                {row.error && (
                  <p className="mt-1 max-w-[260px] text-[11px] text-red-300">{row.error}</p>
                )}
              </td>

              <td className={`${cellClass} text-gray-400`}>{row.triggeredBy}</td>

              <td className={`${cellClass} text-gray-500`}>
                {/*
                  Blank until the Resend webhook is registered in this environment — a pending ops
                  step, not a fault. Until then every row legitimately stops at "accepted".
                */}
                <Timestamp iso={row.providerEventAt} fallback="Awaiting webhook" />
              </td>

              <td className={`${cellClass} text-right`}>
                {row.user ? (
                  <Link
                    to={`/admin/users/${row.user.id}`}
                    className="whitespace-nowrap text-[11px] text-indigo-400 hover:text-indigo-300"
                  >
                    Open account
                  </Link>
                ) : (
                  <span
                    className="whitespace-nowrap text-[11px] text-gray-600"
                    title="Sent before provisioning created the account, so there is no user to open."
                  >
                    No account
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface AdminSuppressionTableProps {
  suppressions: AdminSuppression[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  includeRemoved: boolean;
}

export const AdminSuppressionTable: React.FC<AdminSuppressionTableProps> = ({
  suppressions,
  isLoading,
  error,
  onRetry,
  includeRemoved,
}) => {
  if (error && suppressions.length === 0) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (isLoading && suppressions.length === 0) {
    return <LoadingState message="Loading suppressions…" />;
  }

  if (suppressions.length === 0) {
    return (
      <EmptyState
        message={includeRemoved ? 'No suppressions on record.' : 'No addresses are suppressed.'}
        hint="Nothing is being silently dropped."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-left text-xs">
        <thead>
          <tr className="border-b border-gray-800 bg-gray-950/40 text-[11px] uppercase tracking-wide text-gray-500">
            <th scope="col" className={headerCellClass}>Address</th>
            <th scope="col" className={headerCellClass}>Why</th>
            <th scope="col" className={headerCellClass}>Since</th>
            <th scope="col" className={headerCellClass}>Cleared</th>
            <th scope="col" className={headerCellClass}>Triggering send</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-800/60">
          {suppressions.map(row => (
            <tr key={row.id ?? row.email} className={row.removedAt ? 'opacity-60' : undefined}>
              <td className={`${cellClass} max-w-[260px]`}>
                <span className="block truncate text-gray-100" title={row.email}>
                  {row.email}
                </span>
              </td>
              <td className={cellClass}>
                <SuppressionBadge origin={row.origin} removedAt={row.removedAt} />
              </td>
              <td className={`${cellClass} text-gray-400`}>
                <Timestamp iso={row.suppressedAt} />
              </td>
              <td className={`${cellClass} text-gray-400`}>
                <Timestamp iso={row.removedAt} fallback="Still suppressed" />
              </td>
              <td className={`${cellClass} font-mono text-[10px] text-gray-600`}>
                {row.sourceEmailId || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
